import React, { CSSProperties, ReactElement } from 'react';

import MultiSelectPills, {
  Props as MultiSelectPillsProps,
} from '@rover/components/src/buttons/MultiSelectPills';
import useTheme from '@rover/kibble/themes/useTheme';
import type { RoverTheme } from '@rover/types/src/RoverTheme';

const getDefaultPillHoverStyle = (theme: RoverTheme): Record<string, string> => ({
  // borderColor needs to have important if not it won't override the pill and selected pill styles
  borderColor: `${theme.colors.interactive_border.primary_base} !important`,
  backgroundColor: `${theme.background.tertiary} !important`,
});

const getDefaultSelectedPillHoverStyle = (theme: RoverTheme): Record<string, string> => ({
  borderColor: `${theme.colors.interactive_text.primary_active} !important`,
  backgroundColor: `${theme.background.tertiary} !important`,
});

const getDefaultPillStyle = (theme: RoverTheme): CSSProperties => ({
  borderColor: theme.colors.interactive_border.primary_base,
  padding: `${theme.space['2x']} ${theme.space['3x']}`,
  height: '40px',
  fontSize: theme.text['200'].fontSize,
  fontWeight: 600,
  borderRadius: theme.radii.primary,
  color: theme.textColor.secondary,
});

const getDefaultSelectedPillStyle = (theme: RoverTheme): CSSProperties => ({
  ...getDefaultPillStyle(theme),
  borderColor: theme.colors.interactive_text.primary_active,
  backgroundColor: theme.background.accent_secondary,
});

const getSmallPillStyle = (theme: RoverTheme): CSSProperties => ({
  ...getDefaultPillStyle(theme),
  fontSize: '14px',
  height: '34px',
});

const getSmallSelectedPillStyle = (theme: RoverTheme): CSSProperties => ({
  ...getDefaultSelectedPillStyle(theme),
  fontSize: '14px',
  height: '34px',
});

type TrainingMultiSelectPillsVariant = 'regular' | 'small';

type DisplayedProperties = {
  displayedPillStyle: CSSProperties;
  displayedSelectedPillStyle: CSSProperties;
};

type TrainingMultiSelectPillsProps = Omit<
  MultiSelectPillsProps,
  'pillStyle' | 'selectedPillStyle' | 'pillHoverStyle' | 'pillSelectedHoverStyle'
> & {
  variant?: TrainingMultiSelectPillsVariant;
};

const getVariantStyles = (
  variant: TrainingMultiSelectPillsVariant,
  theme: RoverTheme
): DisplayedProperties => {
  switch (variant) {
    case 'small':
      return {
        displayedPillStyle: getSmallPillStyle(theme),
        displayedSelectedPillStyle: getSmallSelectedPillStyle(theme),
      };
    default:
      return {
        displayedPillStyle: getDefaultPillStyle(theme),
        displayedSelectedPillStyle: getDefaultSelectedPillStyle(theme),
      };
  }
};

const TrainingMultiSelectPills = ({
  variant,
  ...rest
}: TrainingMultiSelectPillsProps): ReactElement => {
  const theme = useTheme();

  const { displayedPillStyle, displayedSelectedPillStyle } = variant
    ? getVariantStyles(variant, theme)
    : {
        displayedPillStyle: getDefaultPillStyle(theme),
        displayedSelectedPillStyle: getDefaultSelectedPillStyle(theme),
      };

  return (
    <MultiSelectPills
      {...rest}
      pillStyle={displayedPillStyle}
      selectedPillStyle={displayedSelectedPillStyle}
      pillHoverStyle={getDefaultPillHoverStyle(theme)}
      pillSelectedHoverStyle={getDefaultSelectedPillHoverStyle(theme)}
    />
  );
};

export default TrainingMultiSelectPills;
